<template>
  <div class="esign-table-footer">
    <!-- table actions (e.g. Tanda tangan, Refresh); replaces v-data-table's footer.prepend,
         which is not rendered once a page supplies its own footer slot -->
    <div v-if="$slots.default" class="esign-table-footer__actions">
      <slot />
    </div>

    <div class="esign-table-footer__info">
      Total <strong>{{ formatNumber(itemsLength) }}</strong> item
      <span v-if="itemsLength > 0" class="esign-table-footer__range">
        · menampilkan {{ formatNumber(pageStart + 1) }}–{{ formatNumber(pageStop) }}
      </span>
    </div>

    <div class="esign-table-footer__controls">
      <label class="esign-table-footer__size">
        <span>Baris per halaman</span>
        <v-select
          :items="sizeItems"
          :value="itemsPerPage"
          dense
          outlined
          hide-details
          :menu-props="{ offsetY: true }"
          @change="setSize"
        />
      </label>

      <v-pagination
        v-if="pageCount > 1"
        :value="page"
        :length="pageCount"
        :total-visible="totalVisible"
        class="esign-table-footer__pages"
        @input="setPage"
      />

      <label v-if="pageCount > 7" class="esign-table-footer__jump">
        <span>Ke halaman</span>
        <v-text-field
          v-model.number="jumpTo"
          type="number"
          :min="1"
          :max="pageCount"
          dense
          outlined
          hide-details
          @focus="$event.target.select()"
          @keydown.enter="jump"
          @blur="jump"
        />
      </label>
    </div>
  </div>
</template>

<script>
  // Shared footer for every v-data-table (used via the table's `footer` slot):
  // total item count, rows per page (10/20/50/Semua), numbered pages and "go to page".
  export default {
    name: 'EsignTableFooter',

    props: {
      // `props.pagination` / `props.options` from v-data-table's footer slot
      pagination: { type: Object, required: true },
      options: { type: Object, required: true },
      itemsPerPageOptions: { type: Array, default: () => [10, 20, 50, -1] },
    },

    data: () => ({
      jumpTo: null,
    }),

    computed: {
      page () { return this.pagination.page || 1 },
      pageCount () { return this.pagination.pageCount || 0 },
      pageStart () { return this.pagination.pageStart || 0 },
      pageStop () { return this.pagination.pageStop || 0 },
      itemsLength () { return this.pagination.itemsLength || 0 },
      itemsPerPage () { return this.options.itemsPerPage },
      sizeItems () {
        const sizes = this.itemsPerPageOptions.slice()
        // keep a remembered size that is no longer offered (e.g. 25) selectable
        if (this.itemsPerPage != null && !sizes.includes(this.itemsPerPage)) sizes.push(this.itemsPerPage)
        return sizes.map(n => ({ text: n === -1 ? 'Semua' : String(n), value: n }))
      },
      totalVisible () {
        return this.$vuetify.breakpoint.xsOnly ? 5 : 7
      },
    },

    watch: {
      page: {
        immediate: true,
        handler (p) { this.jumpTo = p },
      },
    },

    methods: {
      update (changes) {
        this.$emit('update:options', Object.assign({}, this.options, changes))
      },
      setPage (page) {
        if (page !== this.page) this.update({ page })
      },
      setSize (itemsPerPage) {
        this.update({ itemsPerPage, page: 1 })
      },
      jump () {
        const target = Math.min(Math.max(parseInt(this.jumpTo, 10) || 1, 1), this.pageCount)
        this.jumpTo = target
        this.setPage(target)
      },
      formatNumber (n) {
        return Number(n).toLocaleString('id-ID')
      },
    },
  }
</script>

<style>
.esign-table-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 20px;
  padding: 10px 16px;
  border-top: 1px solid var(--esign-border);
  font-size: 13px;
  color: var(--esign-text-muted);
}

.esign-table-footer__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.esign-table-footer__info strong {
  color: var(--esign-text);
  font-weight: 700;
}

.esign-table-footer__controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 16px;
}

.esign-table-footer__size,
.esign-table-footer__jump {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.v-application .esign-table-footer__size .v-select {
  width: 116px;
  flex: 0 0 116px;
}

.v-application .esign-table-footer__jump .v-text-field {
  width: 84px;
  flex: 0 0 84px;
}

.v-application .esign-table-footer .v-input input,
.v-application .esign-table-footer .v-select__selection {
  font-size: 13px !important;
}

/* a global rule lets select text wrap ("Sem / ua"); keep it on one line */
.v-application .esign-table-footer .v-select__selection--comma {
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis;
  margin: 0 4px 0 0;
}

/* same height as the page buttons */
.v-application .esign-table-footer .v-input.v-input--dense .v-input__slot {
  min-height: 32px !important;
  height: 32px;
}

.v-application .esign-table-footer .v-input.v-input--dense .v-input__append-inner {
  margin-top: 4px !important;
}

/* Page buttons */
.v-application .esign-table-footer__pages .v-pagination {
  justify-content: flex-start;
}

.v-application .esign-table-footer__pages .v-pagination__item,
.v-application .esign-table-footer__pages .v-pagination__navigation {
  height: 32px;
  min-width: 32px;
  margin: 2px;
  border-radius: var(--esign-radius-sm);
  box-shadow: none;
  border: 1px solid var(--esign-border);
  font-size: 13px;
  font-weight: 600;
}

.v-application .esign-table-footer__pages .v-pagination__item--active {
  border-color: var(--esign-primary);
}

.v-application .esign-table-footer__pages .v-pagination__more {
  height: 32px;
  width: 24px;
  margin: 2px;
}

@media (max-width: 600px) {
  .esign-table-footer,
  .esign-table-footer__controls {
    justify-content: center;
  }

  .esign-table-footer__info {
    width: 100%;
    text-align: center;
  }

  .v-application .esign-table-footer .v-input input {
    font-size: 16px !important;
  }
}
</style>
